export type { ChatMessage, RepoContext } from '../../models/orchestrator-surface.model';
export { compactChatMessages, normalizeRepoContext } from '../../models/orchestrator-surface.model';
import { extractPositionsLayout } from '../../utils/extract-positions-layout.util';

export type ArchitectureNodeKind =
  | 'service'
  | 'database'
  | 'queue'
  | 'external'
  | 'client'
  | 'boundary'
  | 'note';

export type ArchitectureNode = {
  id: string;
  title: string;
  kind?: ArchitectureNodeKind;
  accent?: string;
};

export type ArchitectureEdge = {
  id: string;
  source: string;
  target: string;
  label?: string;
};

export type ArchitectureDiagram = {
  version?: 1;
  nodes: ArchitectureNode[];
  edges: ArchitectureEdge[];
};

export type ArchitectureLayout = {
  positions: Record<string, { x: number; y: number }>;
};

export type InkPoint = { x: number; y: number; p?: number; t?: number };
export type InkStroke = { id: string; color: string; size: number; points: InkPoint[] };
export type InkDocument = { strokes: InkStroke[] };

// ─── Private helpers ──────────────────────────────────────────────────────────

function slugifyStableId(input: string): string {
  const s = String(input ?? '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  return s || 'node';
}

function tokenSet(input: string): Set<string> {
  const raw = String(input ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const parts = raw ? raw.split(/\s+/g) : [];
  return new Set(parts.filter(Boolean));
}

function expandedTokensForNode(title: string, kind?: string): Set<string> {
  const tokens = tokenSet(title);
  // Common expansions to improve matching (e.g. "database" <-> "db").
  if (tokens.has('database')) tokens.add('db');
  if (tokens.has('db')) tokens.add('database');
  if (tokens.has('api')) tokens.add('service');
  if (tokens.has('service')) tokens.add('api');
  if (tokens.has('mobile')) tokens.add('client');
  if (tokens.has('client')) tokens.add('app');
  if (tokens.has('cloud')) tokens.add('external');
  if (tokens.has('storage')) tokens.add('external');
  if (tokens.has('queue')) tokens.add('events');
  if (tokens.has('event')) tokens.add('events');
  if (tokens.has('events')) tokens.add('queue');
  if (tokens.has('boundary')) tokens.add('trust');
  if (tokens.has('trust')) tokens.add('boundary');

  const k = String(kind ?? '').toLowerCase();
  if (k) tokens.add(k);
  if (k === 'database') tokens.add('db');
  if (k === 'client') tokens.add('app');
  if (k === 'service') tokens.add('api');
  return tokens;
}

function scoreCandidateId(nodeTokens: Set<string>, candidateId: string, kind?: string): number {
  const candTokens = tokenSet(candidateId.replace(/_/g, ' '));
  let score = 0;
  for (const t of candTokens) {
    if (nodeTokens.has(t)) score += 3;
  }

  // Small hinting based on kind.
  const k = String(kind ?? '').toLowerCase();
  if (k === 'database' && (candTokens.has('db') || candTokens.has('database'))) score += 2;
  if (k === 'client' && (candTokens.has('client') || candTokens.has('app') || candTokens.has('mobile'))) score += 2;
  if (k === 'service' && (candTokens.has('api') || candTokens.has('service'))) score += 2;
  if (k === 'external' && (candTokens.has('external') || candTokens.has('cloud') || candTokens.has('storage'))) score += 2;
  if (k === 'queue' && (candTokens.has('queue') || candTokens.has('event') || candTokens.has('events'))) score += 2;
  if (k === 'boundary' && (candTokens.has('boundary') || candTokens.has('trust'))) score += 2;

  // Prefer shorter IDs slightly (stability/readability).
  score -= Math.min(2, Math.floor(candidateId.length / 32));
  return score;
}

// ─── Exported normalizers ─────────────────────────────────────────────────────

export function normalizeArchitectureDiagram(
  diagram: ArchitectureDiagram | null,
  opts?: { layout?: ArchitectureLayout | null },
): { diagram: ArchitectureDiagram | null; changed: boolean } {
  if (!diagram) return { diagram: null, changed: false };
  const nodesIn = Array.isArray(diagram.nodes) ? diagram.nodes : [];
  const edgesIn = Array.isArray(diagram.edges) ? diagram.edges : [];

  const usedIds = new Set<string>();
  const nodesOut: any[] = [];

  const layoutIds = new Set(Object.keys(opts?.layout?.positions ?? {}));
  const edgeEndpointIds = new Set(
    edgesIn
      .flatMap((e: any) => [String(e?.source ?? '').trim(), String(e?.target ?? '').trim()])
      .filter(Boolean),
  );

  const candidateIds = Array.from(new Set<string>([...layoutIds, ...edgeEndpointIds]))
    .map((s) => String(s ?? '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  let changed = false;

  // First pass: keep any explicit ids.
  for (const n of nodesIn) {
    const id = String((n as any)?.id ?? '').trim();
    if (id) usedIds.add(id);
  }

  const remainingCandidates = () => candidateIds.filter((id) => !usedIds.has(id));

  for (const n of nodesIn) {
    const prevId = String((n as any)?.id ?? '').trim();
    const title = String((n as any)?.title ?? (n as any)?.name ?? '').trim();
    const kind = String((n as any)?.kind ?? '').trim();

    let id = prevId;
    if (!id) {
      // Prefer known IDs from layout/edges when possible.
      const nodeTokens = expandedTokensForNode(title, kind);
      const candidates = remainingCandidates();

      let bestId: string | null = null;
      let bestScore = -Infinity;
      for (const cid of candidates) {
        const s = scoreCandidateId(nodeTokens, cid, kind);
        if (s > bestScore) {
          bestScore = s;
          bestId = cid;
        }
      }

      if (bestId && bestScore >= 3) {
        id = bestId;
      } else {
        id = slugifyStableId(title || kind || 'node');
        // Ensure uniqueness.
        let suffix = 2;
        while (usedIds.has(id)) {
          id = `${slugifyStableId(title || kind || 'node')}_${suffix++}`;
        }
      }
      changed = true;
    }

    usedIds.add(id);
    nodesOut.push({
      ...(n as any),
      id,
      title: title || String((n as any)?.title ?? (n as any)?.name ?? 'Node'),
      kind: kind || (n as any)?.kind,
    });
  }

  // Normalize edges: ensure ids, and only keep edges that reference existing nodes.
  const nodeIdSet = new Set(nodesOut.map((n) => String(n?.id ?? '').trim()).filter(Boolean));
  const edgeIds = new Set<string>();
  const edgesOut: any[] = [];
  for (const e of edgesIn) {
    const source = String((e as any)?.source ?? '').trim();
    const target = String((e as any)?.target ?? '').trim();
    if (!source || !target) {
      changed = true;
      continue;
    }
    if (!nodeIdSet.has(source) || !nodeIdSet.has(target)) {
      changed = true;
      continue;
    }
    let id = String((e as any)?.id ?? '').trim();
    if (!id) {
      id = `e_${source}_${target}`;
      let suffix = 2;
      while (edgeIds.has(id)) id = `e_${source}_${target}_${suffix++}`;
      changed = true;
    }
    edgeIds.add(id);
    edgesOut.push({ ...(e as any), id, source, target });
  }

  return {
    diagram: {
      version: 1,
      nodes: nodesOut as any,
      edges: edgesOut as any,
    } as ArchitectureDiagram,
    changed,
  };
}

export function extractDiagram(payload: any): ArchitectureDiagram | null {
  const candidate = payload?.diagram && typeof payload.diagram === 'object' ? payload.diagram : payload;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
  const nodes = Array.isArray((candidate as any).nodes) ? (candidate as any).nodes : [];
  const edges = Array.isArray((candidate as any).edges) ? (candidate as any).edges : [];
  return { version: 1, nodes, edges } as ArchitectureDiagram;
}

export function extractLayout(payload: any): ArchitectureLayout | null {
  const layout = extractPositionsLayout(payload, 'layout');
  return layout ? (layout as ArchitectureLayout) : null;
}

export function extractInk(payload: any): InkDocument | null {
  const candidate = payload?.ink && typeof payload.ink === 'object' ? payload.ink : payload;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
  const strokes = Array.isArray((candidate as any).strokes) ? (candidate as any).strokes : [];
  return { strokes } as InkDocument;
}
